<script lang="ts" setup>
import { autoUpdate, offset, useFloating } from '@floating-ui/vue';
import { onMounted, onUnmounted, ref } from 'vue';

import MapInfo from '#nora-ui/map/components/controls/map-info/MapInfo.vue';
import MapContextMenu from '#nora-ui/map/components/controls/MapContextMenu.vue';
import MapCrosshair from '#nora-ui/map/components/controls/MapCrosshair.vue';
import DrawToolViewContainer from '#nora-ui/map/components/drawtools/DrawToolViewContainer.vue';
import type { UseMapContextMenu } from '#nora-ui/map/composables/useMapContextMenu';
import { useMapContextMenu } from '#nora-ui/map/composables/useMapContextMenu';
import { useMapStore } from '#nora-ui/map/stores/map-store';

interface MapProps {
  mapId: string;
}

const props = defineProps<MapProps>();
const mapStore = useMapStore();

const map = mapStore.getMapByIdOrThrow(props.mapId);

const isReady = ref(false);
const mapDomElement = ref<HTMLElement>();
const drawToolPositionReference = ref<HTMLElement>();
const drawToolViewContainerReference = ref<HTMLElement>();
const contextMenuRef = ref();

let contextMenu: UseMapContextMenu | null = null;

const { floatingStyles } = useFloating(
  drawToolPositionReference,
  drawToolViewContainerReference,
  {
    whileElementsMounted: autoUpdate,
    placement: 'top',
    middleware: [offset(10)],
    transform: false,
  },
);

async function initMap() {
  if (!mapDomElement.value) {
    return;
  }

  await map.setup(mapDomElement.value);
}

function setupContextMenu() {
  const viewport = map.getOlMapOrThrow().getViewport();

  contextMenu = useMapContextMenu({
    viewport,
    onOpen: (event: MouseEvent) => {
      contextMenuRef.value?.showMenu(event);
    },
  });

  contextMenu.setup();
}

onMounted(async () => {
  await initMap();
  setupContextMenu();

  isReady.value = true;
});

onUnmounted(() => {
  contextMenu?.cleanup();
});
</script>

<template>
  <div class="relative h-full">
    <div
      ref="mapDomElement"
      class="w-full h-full"
    />

    <template v-if="isReady">
      <MapCrosshair />
      <div
        ref="drawToolPositionReference"
        class="h-10 absolute inset-x-0 bottom-0 pointer-events-none"
      />
      <DrawToolViewContainer
        ref="drawToolViewContainerReference"
        class="z-50"
        :style="floatingStyles"
        :map-id="props.mapId"
      />
      <MapContextMenu
        ref="contextMenuRef"
        :map-id="props.mapId"
      />
      <MapInfo :map-id="props.mapId" />
    </template>
  </div>
</template>
